import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { getPersonContactCardFormGroupFormDataAsPersonProject } from '@app/helpers/form.helper';
import { LegalEntityFormGroup } from '@app/models/form.models';
import {
  LegalEntityRequest,
  ProjectSetupERequest,
  ProjectSetupEResponse,
} from '@app/models/project-setup.models';
import {
  getPersonContactCardFormGroup,
  getPersonContactCardFormValueFromPersonSimpleWithRole,
  PersonContactCardFormGroup,
} from '../../person-contact-card/person-contact-form/person-contact-card-form.helper';
import { LocalizedValue } from '@app/models/shared.models';
import { getLegalEntitiyData } from '../../legal-person-validated-form/legal-person-validated-form.helper';

export interface SectionEFormGroup {
  specialPurpose: FormControl<boolean>;
  legalEntity: FormGroup<LegalEntityFormGroup>;
  contact: FormGroup<PersonContactCardFormGroup>;
  /** Person role entity id used as connectionTypeCode in the API (Section E contact). */
  contactPersonConnectionTypeCodeId: FormControl<string | null>;
}

export function getSectionEFormGroup(
  fb: FormBuilder
): FormGroup<SectionEFormGroup> {
  return fb.nonNullable.group({
    specialPurpose: fb.nonNullable.control<boolean>(false, {
      validators: [Validators.required],
    }),
    legalEntity: getLegalEntitiyData(fb),
    contact: getPersonContactCardFormGroup(
      fb,
      false,
      'KulonlegesCelGazdEgysegKapcs',
    ),
    contactPersonConnectionTypeCodeId: fb.control<string | null>(null),
  });
}

function legalEntityFormToRequest(
  legalEntity: FormGroup<LegalEntityFormGroup>
): LegalEntityRequest {
  const raw = legalEntity.getRawValue();
  return {
    taxNumber: raw.taxNumber ?? '',
    name: raw.name ?? '',
    email: raw.email ?? '',
    country: raw.country ?? '',
    ownershipStructure: raw.ownershipStructure ?? null,
    contactInfo: raw.contactInfo ?? null,
    website: raw.website ?? null,
    teaorNumbers: raw.teaorNumbers ?? null,
    bankAccountNumber: raw.bankAccountNumber ?? null,
    companyTypeId: raw.companyTypeId ?? null,
    address: {
      countryCode: raw.address?.countryCode ?? null,
      zipCode: raw.address?.zipCode ?? null,
      city: raw.address?.city ?? null,
      publicSpace: raw.address?.publicSpace ?? null,
      publicSpaceType: raw.address?.publicSpaceType ?? null,
      houseNumber: raw.address?.houseNumber ?? null,
      doorOrFloor: raw.address?.doorOrFloor ?? null,
    },
  };
}

export function isSectionESpecialPurposeYes(value: unknown): boolean {
  return value === true || value === 'true';
}

export function applySectionENestedControlsEnabledState(
  form: FormGroup<SectionEFormGroup>,
  specialPurposeYes: boolean,
): void {
  if (specialPurposeYes) {
    form.controls.legalEntity.enable({ emitEvent: false });
    form.controls.contact.enable({ emitEvent: false });
    form.controls.contactPersonConnectionTypeCodeId.enable({ emitEvent: false });
  } else {
    form.controls.legalEntity.disable({ emitEvent: false });
    form.controls.contact.disable({ emitEvent: false });
    form.controls.contactPersonConnectionTypeCodeId.disable({ emitEvent: false });
  }
}

export function getSectionEUpdateData(
  formGroup: FormGroup<SectionEFormGroup>
): ProjectSetupERequest {
  const specialPurpose = formGroup.controls.specialPurpose.value;

  if (!isSectionESpecialPurposeYes(specialPurpose)) {
    return {
      isSpecialPurposeCompanyBetweenOwnerAndInvestor: false,
      persons: [],
      legalEntitySpecialPurpose: undefined,
    };
  }

  const contactPerson = getPersonContactCardFormGroupFormDataAsPersonProject(
    formGroup.controls.contact
  );
  const connectionTypeCodeId =
    formGroup.controls.contactPersonConnectionTypeCodeId.value;
  return {
    isSpecialPurposeCompanyBetweenOwnerAndInvestor: true,
    persons: [
      {
        ...contactPerson,
        connectionTypeCode: connectionTypeCodeId ?? contactPerson.connectionTypeCode,
      },
    ],
    legalEntitySpecialPurpose: legalEntityFormToRequest(
      formGroup.controls.legalEntity
    ),
  };
}

export function patchSectionEForm(
  form: FormGroup<SectionEFormGroup>,
  response: ProjectSetupEResponse,
  nameTitles?: readonly LocalizedValue[],
): void {
  let legalPerson = null;
  if (response.legalEntitySpecialPurpose?.length > 0) {
    legalPerson = response.legalEntitySpecialPurpose[0];
  }
  let person = null;
  if (response.persons?.length > 0) {
    person = response.persons[0];
  }

  form.controls.specialPurpose.setValue(
    response.isSpecialPurposeCompanyBetweenOwnerAndInvestor === null
      ? false
      : response.isSpecialPurposeCompanyBetweenOwnerAndInvestor
  );
  form.controls.contact.patchValue(
    getPersonContactCardFormValueFromPersonSimpleWithRole(person, nameTitles)
  );
  form.controls.contactPersonConnectionTypeCodeId.setValue(
    person?.connection?.id ?? null
  );

  if (legalPerson) {
    const addr = legalPerson.address;
    form.controls.legalEntity.patchValue({
      taxNumber: legalPerson.taxNumber,
      name: legalPerson.name,
      email: legalPerson.email,
      country: legalPerson.country,
      ownershipStructure: legalPerson.ownershipStructure,
      contactInfo: legalPerson.contactInfo ?? '',
      website: legalPerson.website ?? '',
      teaorNumbers: legalPerson.teaorNumbers ?? null,
      bankAccountNumber: legalPerson.bankAccountNumber ?? '',
      companyTypeId: legalPerson.companyType?.id ?? '',
      address: {
        countryCode: addr?.countryCode ?? '',
        zipCode: addr?.zipCode ?? '',
        city: addr?.city ?? '',
        publicSpace: addr?.publicSpace ?? '',
        publicSpaceType: addr?.publicSpaceType ?? '',
        houseNumber: addr?.houseNumber ?? '',
        doorOrFloor: addr?.doorOrFloor ?? '',
      },
    });
  }

  const yes = isSectionESpecialPurposeYes(form.controls.specialPurpose.value);
  applySectionENestedControlsEnabledState(form, yes);
}
